import { com } from '@eidu/web'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import getEntitiesBatch, { MAX_NUM_IDS_PER_BATCH } from './api/entity/data/getEntitiesBatch'
import getEntitiesFromResponses from '../domain/entity/getEntitiesFromResponses'
import EntityWithLabelAndRelated from '../domain/entity/EntityWithLabelAndRelated'
import EqualityHashMap from '../util/EqualityHashMap'
import chunked from '../util/chunked'
import equalityHashMapStructuralSharing from './equalityHashMapStructuralSharing'
import { entityKeys, entityTypeKeys } from './queryKeys'
import { fetchAllEntityTypes } from './useEntityTypes'
import EntityId = com.eidu.sharedlib.entity.EntityId
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import EntityType = com.eidu.sharedlib.entity.type.EntityType

export const getOrFetchTypes = async (
  queryClient: ReturnType<typeof useQueryClient>
): Promise<ReadonlyMap<EntityTypeId, EntityType>> =>
  queryClient.fetchQuery({
    queryKey: entityTypeKeys.list(),
    queryFn: fetchAllEntityTypes,
  })

export const fetchEntitiesByIds = async (
  ids: readonly EntityId[],
  types: ReadonlyMap<EntityTypeId, EntityType>
): Promise<ReadonlyMap<EntityId, EntityWithLabelAndRelated | null>> => {
  if (ids.length === 0) return new EqualityHashMap()
  const batches = chunked(ids, MAX_NUM_IDS_PER_BATCH)
  const responses = await Promise.all(
    batches.map((batch) =>
      getEntitiesBatch({ ids: batch, withRelatedEntities: true, pageIndex: 0, pageSize: batch.length })
    )
  )
  const nullByEntityId: [EntityId, null][] = batches.flat().map((id) => [id, null])
  const fromResponses: [EntityId, EntityWithLabelAndRelated][] = [...getEntitiesFromResponses(responses, types)]
  return new EqualityHashMap([...nullByEntityId, ...fromResponses])
}

export const useFetchEntity = () => {
  const queryClient = useQueryClient()
  return useCallback(
    async (id: EntityId): Promise<EntityWithLabelAndRelated | null> =>
      queryClient.fetchQuery({
        queryKey: entityKeys.detail(id),
        queryFn: async () => {
          const types = await getOrFetchTypes(queryClient)
          const result = await fetchEntitiesByIds([id], types)
          return result.get(id) ?? null
        },
      }),
    [queryClient]
  )
}

export const useFetchEntities = () => {
  const queryClient = useQueryClient()
  return useCallback(
    async (ids: readonly EntityId[]): Promise<ReadonlyMap<EntityId, EntityWithLabelAndRelated | null>> => {
      if (ids.length === 0) return new EqualityHashMap()
      const types = await getOrFetchTypes(queryClient)
      const fetched = await fetchEntitiesByIds(ids, types)
      fetched.forEach((entity, id) => queryClient.setQueryData(entityKeys.detail(id), entity ?? null))
      return fetched
    },
    [queryClient]
  )
}

export const useEntitiesByIds = (ids: readonly EntityId[]) => {
  const queryClient = useQueryClient()
  const sortedKey = ids
    .map((it) => it.toString())
    .sort()
    .join(',')
  const query = useQuery({
    queryKey: [...entityKeys.all, 'byIds', sortedKey],
    queryFn: async () => {
      const types = await getOrFetchTypes(queryClient)
      const fetched = await fetchEntitiesByIds(ids, types)
      fetched.forEach((entity, id) => queryClient.setQueryData(entityKeys.detail(id), entity ?? null))
      return fetched
    },
    enabled: ids.length > 0,
    structuralSharing: equalityHashMapStructuralSharing,
  })
  return {
    entities: query.data,
    isLoading: query.isLoading,
    error: query.error,
  }
}
